import { Cart } from "../../models/cart"
import { Product } from "../../models/product"

type CheckoutAdditionOption = {
  id: string
  additions: {
    id: string
    count: number
    addition_options: CheckoutAdditionOption[]
  }[]
}

export type CheckoutProduct = {
  id: string
  count: number
  recommended_product_id?: string
  addition_options?: CheckoutAdditionOption[]
}

export const getCheckoutProductsFromCart = (cart: Cart): CheckoutProduct[] => {
  const checkoutProducts: CheckoutProduct[] = []
  cart.items.forEach((product: Product) => {
    if (product.quantity === 0) {
      return
    }
    let finalProduct: CheckoutProduct = {
      id: product.id,
      ...(product.recommendedProductId && {
        recommended_product_id: product.recommendedProductId,
      }),
      count: product.quantity,
    }

    const additionOptions = (product.addition_options || []).map((ao: any) => {
      return {
        id: ao.id,
        additions: ao.additions
          .map((a: any) => {
            if (a.count > 0) {
              return { id: a.id, count: a.count, addition_options: [] }
            }
          })
          .filter(Boolean),
      }
    })
    finalProduct.addition_options = additionOptions

    checkoutProducts.push(finalProduct)
  })

  return checkoutProducts
}

const phonenumberIsCorrectLength = (length: number, phonenumber: string) => {
  const regex = new RegExp(`^[^a-zA-Z]{${length}}$`)
  return regex.test(phonenumber)
}

const phonenumberIsWithinRange = (phonenumber: string) => {
  return +phonenumber > 100000 && +phonenumber < 9999999999
}

const phonenumberIsValidUnformatted = (phonenumber: string) => {
  const regex = new RegExp(`^[^a-zA-Z]*$`)
  return regex.test(phonenumber) && phonenumberIsWithinRange(phonenumber)
}

const phonenumberIsValid = (phonenumber: string, length: number) => {
  return (
    phonenumberIsCorrectLength(length, phonenumber) &&
    phonenumberIsWithinRange(phonenumber)
  )
}

// Getting phonenumber length from "pattern" in  https://en.wikipedia.org/wiki/North_American_Numbering_Plan#Countries_and_territories
export const testPhonenumber = (prefix: string, phonenumber: string) => {
  switch (prefix) {
    case "+298":
    case "+299":
    case "+376":
    case "+508":
    case "+672":
    case "+681":
    case "+682":
    case "+689":
      return phonenumberIsValid(phonenumber, 6)
    case "+220":
    case "+238":
    case "+239":
    case "+245":
    case "+246":
    case "+248":
    case "+269":
    case "+291":
    case "+297":
    case "+354":
    case "+501":
    case "+592":
    case "+597":
    case "+599":
    case "+673":
    case "+674":
    case "+675":
    case "+676":
    case "+677":
    case "+678":
    case "+679":
    case "+960":
      return phonenumberIsValid(phonenumber, 7)
    case "+45":
    case "+47":
    case "+216":
    case "+222":
    case "+223":
    case "+225":
    case "+226":
    case "+227":
    case "+228":
    case "+229":
    case "+230":
    case "+232":
    case "+235":
    case "+236":
    case "+237":
    case "+241":
    case "+253":
    case "+257":
    case "+266":
    case "+267":
    case "+268":
    case "+350":
    case "+356":
    case "+357":
    case "+359":
    case "+370":
    case "+371":
    case "+372":
    case "+373":
    case "+374":
    case "+378":
    case "+382":
    case "+383":
    case "+386":
    case "+387":
    case "+389":
    case "+502":
    case "+503":
    case "+504":
    case "+505":
    case "+506":
    case "+507":
    case "+509":
    case "+591":
    case "+598":
    case "+670":
    case "+687":
    case "+852":
    case "+853":
    case "+855":
    case "+95":
    case "+961":
    case "+965":
    case "+968":
    case "+973":
    case "+974":
    case "+975":
    case "+976":
    case "+993":
      return phonenumberIsValid(phonenumber, 8)
    case "+46":
    case "+211":
    case "+212":
    case "+213":
    case "+218":
    case "+221":
    case "+224":
    case "+231":
    case "+233":
    case "+240":
    case "+242":
    case "+243":
    case "+244":
    case "+250":
    case "+251":
    case "+252":
    case "+254":
    case "+255":
    case "+256":
    case "+258":
    case "+260":
    case "+261":
    case "+263":
    case "+264":
    case "+265":
    case "+27":
    case "+31":
    case "+32":
    case "+33":
    case "+34":
    case "+351":
    case "+352":
    case "+353":
    case "+355":
    case "+375":
    case "+377":
    case "+380":
    case "+381":
    case "+385":
    case "+40":
    case "+41":
    case "+420":
    case "+421":
    case "+423":
    case "+43":
    case "+48":
    case "+51":
    case "+56":
    case "+590":
    case "+593":
    case "+594":
    case "+595":
    case "+596":
    case "+60":
    case "+61":
    case "+62":
    case "+64":
    case "+886":
    case "+93":
    case "+94":
    case "+962":
    case "+966":
    case "+967":
    case "+970":
    case "+971":
    case "+972":
    case "+992":
    case "+994":
    case "+995":
    case "+996":
    case "+998":
      return phonenumberIsValid(phonenumber, 9)
    case "+49":
    case "+1":
    case "+44":
    case "+20":
    case "+234":
    case "+30":
    case "+39":
    case "+52":
    case "+54":
    case "+57":
    case "+58":
    case "+63":
    case "+7":
    case "+81":
    case "+82":
    case "+84":
    case "+856":
    case "+880":
    case "+90":
    case "+91":
    case "+92":
    case "+963":
    case "+964":
    case "+977":
      return phonenumberIsValid(phonenumber, 10)
    case "+872":
    case "+685":
    case "+86":
    case "+290":
    case "+500":
    case "+262":
    case "+537":
    case "+247":
    case "+690":
    case "+379":
    case "+358":
    case "+55":
    case "+683":
    case "+688":
    case "+686":
      return phonenumberIsValidUnformatted(phonenumber)
    default:
      return phonenumberIsValidUnformatted(phonenumber)
  }
}
